import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType, ObjectDocumentAiResultType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import { hasDocumentAiValues } from './documentAiUtils';

type DocumentAiPhase = 'idle' | 'uploading' | 'processing';

interface UseUploadDocumentAiInfoParams {
    objectId: number;
    onParsed: (
        parsed: ObjectDocumentAiResultType['document'],
        cloudFile: LeadCloudFileType,
        file: File,
    ) => void;
}

const useUploadDocumentAiInfo = ({ objectId, onParsed }: UseUploadDocumentAiInfoParams) => {
    const { t } = useTranslation(['Objects']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [phase, setPhase] = useState<DocumentAiPhase>('idle');
    const abortControllerRef = useRef<AbortController | null>(null);

    const processingLabel =
        phase === 'uploading'
            ? t('documents.uploadAi.loadingUploadFile')
            : t('documents.uploadAi.loadingAiProcess');

    const resetInput = () => {
        if (refInputFile.current) {
            refInputFile.current.value = '';
        }
    };

    const handleOpenClick = () => {
        if (isBusy) return;
        refInputFile.current?.click();
    };

    const handleCancel = () => {
        if (phase !== 'processing') return;
        abortControllerRef.current?.abort();
    };

    const handleChangeFile = async () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;

        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            showToast(t('messSaveObjectFirst'), 'warning');
            resetInput();
            return;
        }

        setIsBusy(true);
        setPhase('uploading');
        setUploadProgress(1);

        try {
            const filesMeta = [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    fileSize: file.size,
                },
            ];

            const uploadConfigs = await getCloudflareUploadUrls(filesMeta, { objectId: targetObjectId });
            const uploadConfig = uploadConfigs?.[0];
            if (!uploadConfig) {
                throw new Error('cloud_upload_urls_missing');
            }

            const uploaded = await uploadFileWithProgress(file, uploadConfig, setUploadProgress);
            if (!uploaded.success || !uploaded.config?.publicUrl) {
                throw new Error('cloud_upload_failed');
            }

            setUploadProgress(100);
            setPhase('processing');
            abortControllerRef.current = new AbortController();

            const job = await runAiJobWithPolling<
                { fileUrl: string; fileName: string },
                ObjectDocumentAiResultType
            >({
                requestType: 'objects.document.parse-file',
                payload: {
                    fileUrl: uploaded.config.publicUrl,
                    fileName: file.name,
                },
                pollIntervalMs: 2000,
                timeoutMs: 180000,
                signal: abortControllerRef.current.signal,
            });

            const parsedDocument = job.result?.document || {
                proposalType: '',
                proposalName: '',
                proposalNumber: '',
                proposalDt: null,
                proposalAmount: '',
                generationComment: '',
                pricesInUsd: null,
                proposalVat: null,
                markPaid: null,
            };

            if (!hasDocumentAiValues(parsedDocument)) {
                showToast(t('documents.uploadAi.messEmpty'), 'warning');
                return;
            }

            onParsed(parsedDocument, uploaded.config, file);
            showToast(t('documents.uploadAi.messLoaded'), 'success');
        } catch (error) {
            const message = (error as Error)?.message || '';
            if (message === 'ai_job_aborted' || message === 'canceled') {
                showToast(t('documents.uploadAi.messCanceled'), 'warning');
            } else {
                console.error(error);
                showToast(t('documents.uploadAi.messParseError'), 'danger');
            }
        } finally {
            abortControllerRef.current = null;
            setIsBusy(false);
            setPhase('idle');
            setUploadProgress(false);
            resetInput();
        }
    };

    return {
        refInputFile,
        isBusy,
        uploadProgress,
        phase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useUploadDocumentAiInfo;
